<script lang="ts">
	import { goto } from '$app/navigation';
	import { contentSlide } from '$lib/animations/ContentTransitions';
	import { Carousel, CarouselItem } from '$lib/builders/carousel/Carousel.svelte';
	import { Drag } from '$lib/builders/carousel/modifiers/Drag';
	import type { EventGalleryData } from '$lib/queries/EventGalleryQuery';
	import type { FeedItemData } from '$lib/types/Feed';
	import { PersonalizationTypes } from '$lib/types/Personalization';
	import { DatoImage, SideBar } from '@atoms';
	import { Theme } from '@tools';
	import type { ResponsiveImageData } from '@tools/cms';
	import { personalizationTracking } from '@tools/PersonalizationTracking';
	import gsap from 'gsap';
	import GenreTags from './GenreTags.svelte';

	let { data, useBanner = true }: { data: EventGalleryData; useBanner?: boolean } = $props();
	type ValidatedEvent = FeedItemData & { poster: { responsiveImage: ResponsiveImageData } };
	let events: ValidatedEvent[] = data.events.filter((e: FeedItemData) => e.poster?.responsiveImage);

	while (events.length <= 4) {
		events = events.concat(events);
	}

	const carousel = Carousel.from<ValidatedEvent>(events, {
		drag: true,
		snapPixels: true,
		scroll: { pauseOnHover: false, auto: -1.25 }
	});

	function onPathClick(path?: string) {
		if (!path) return;
		// ignore clicks if the carousel is being dragged
		if (carousel.getModifier(Drag)?.isClickable()) {
			trackClick();
			goto(path);
		}
	}

	function trackClick() {
		if (data.galleryType === 'genre' && data.genre) {
			personalizationTracking.track(PersonalizationTypes.GENRE, [data.genre.id]);
		}
		personalizationTracking.track(PersonalizationTypes.GALLERY, { type: data.galleryType });
	}

	const animationProps = {
		duration: 0.8,
		ease: 'expo.out'
	};

	function onEnter(item: CarouselItem<ValidatedEvent>) {
		const info = item.node.querySelector('.info');
		const img = item.node.querySelector('img');

		gsap.to(img, { scale: 1.1, ...animationProps });
		gsap.to(info, { y: -30, ...animationProps });
	}
	function onLeave(item: CarouselItem<ValidatedEvent>) {
		const info = item.node.querySelector('.info');
		const img = item.node.querySelector('img');

		gsap.to(img, { scale: 1, ...animationProps });
		gsap.to(info, { y: 0, ...animationProps });
	}

	const theme = new Theme(data.colorTheme);
</script>

<section class="block:hero-gallery block:large" style={theme.css}>
	{#if useBanner}
		<SideBar title={true} />
	{/if}

	<div class="content">
		<h3 class="title text:h3">{data.title}</h3>
		<div class="gallery-container" use:carousel.useContainer {...carousel.attr.container}>
			{#each carousel.items as item}
				<div
					class="item"
					role="button"
					tabindex="0"
					onmouseenter={() => onEnter(item)}
					onmouseleave={() => onLeave(item)}
					onclick={() => onPathClick(item.data?.path)}
					use:item.useItem
					{...item.attr}
				>
					<DatoImage responsiveImage={item.data.poster.responsiveImage} lazy={item.index >= 2} />
					<div class="cover"></div>
					<div class="info" in:contentSlide|global out:contentSlide|global>
						<h4 class="text:h2">{item.data.title}</h4>
						{#if item.data.genres}
							<div class="genres">
								<GenreTags data={item.data.genres} />
							</div>
						{/if}
					</div>
				</div>
			{/each}
		</div>
	</div>
</section>

<style lang="scss">
	section {
		position: relative;
		display: flex;
		background-color: var(--theme-primary);
		--in-view: 1.2;
		aspect-ratio: var(--in-view) / #{$ratio1};
		@include fullBleedBlock;

		@include md {
			--in-view: 2.5;
		}

		@include lg {
			--in-view: 2.75;
		}

		@include xl {
			--in-view: 3;
		}
	}

	.title {
		position: absolute;
		top: 0;
		text-transform: uppercase;
		color: $colorBackground;
		padding-top: $gap6;
		padding-left: var(--gutter);
		z-index: 1;

		@include md {
			padding-top: 1.5vw;
		}
	}

	.info {
		position: absolute;
		display: flex;
		width: 100%;
		padding: 0 8vw;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		left: 0;
		bottom: 0;
		padding-bottom: 26vw;
		color: $colorBackground;
		z-index: 4;
		gap: $gap2;

		@include md {
			padding: 0 2.5vw;
			padding-bottom: 8vw;
			gap: $gap3;
		}
	}

	.genres {
		display: flex;
		flex-wrap: wrap;
		gap: 0.4em;
		text-transform: lowercase;
		justify-content: center;

		:global(a) {
			color: $colorBackground;
		}
	}

	h4 {
		text-align: center;
		line-height: 1.1em;
		display: -webkit-box;
		overflow-wrap: break-word;
		word-wrap: break-word;
		overflow: hidden;
		text-overflow: ellipsis;
		-webkit-box-orient: vertical;
		max-height: #{1.1 * 2}em;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		max-width: 100%;
	}

	.gallery-container {
		position: absolute;
		display: flex;
		height: 100%;
		width: 100%;
		overflow: hidden;
		background-color: var(--theme-secondary);
	}

	.item {
		display: flex;
		flex-direction: column;
		align-items: stretch;
		clip-path: ellipse(50% 50% at 50% 50%);
		position: relative;

		height: 100%;
		width: calc(100vw / var(--in-view));
		flex: 0 0 auto;
	}

	.content {
		display: flex;
		flex: 1 1 auto;
	}

	.cover {
		pointer-events: none;
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 100%;
		z-index: 1;
		background-color: rgba(0, 0, 0, 0.2);
		display: block;
	}
</style>
